import React from 'react';

import {Atom, Plugins, withSystem} from '@packages/systems/wfdl';
import {TListOfElement, TTypeConstructor} from '@packages/systems/wfdl-ast';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';
import {
  type EvaluatedNodeData,
  getBooleanOrElse,
  getChildren,
  getInPojo,
} from '@packages/utilities/data-utils';
import {
  MODAL_LOCK_SCROLL_ATTRIBUTE,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {usePreviewMode, useRuntimeEnvironment} from '@packages/systems/render';
import {getModalElementDomId} from '@packages/systems/overlays';
import {POPOVER_MODE_MANUAL} from '@packages/systems/plugins-utils/Popover';
import {
  DesignerOverlayTarget,
  type OverlayElement,
} from '@packages/systems/designer-canvas-overlays';

const Modal = Atom.createRender(
  ({
    attributes,
    classes,
    data,
    elementId,
    environmentContext,
    generateAttributes,
    nativeIdPath,
    renderChild,
    context,
  }) => {
    const previewMode = usePreviewMode();
    const runtimeEnvironment = useRuntimeEnvironment();
    const domIdAttributes = attributes.set(
      'id',
      getModalElementDomId(elementId, nativeIdPath, environmentContext)
    );
    // Not in the LLM render: no runtime reads it there, and the return trip
    // through `html-to-webflow` would turn it into an authored custom attribute.
    const idAttributes =
      getBooleanOrElse(getInPojo(data, ['lockScroll']), false) &&
      runtimeEnvironment !== WebflowEnvironments.LLM
        ? domIdAttributes.set(MODAL_LOCK_SCROLL_ATTRIBUTE, '')
        : domIdAttributes;
    // A manual popover is what lets the canvas show the dialog in the top
    // layer without making the rest of the page inert.
    const modalAttributes =
      runtimeEnvironment === WebflowEnvironments.DESIGNER && !previewMode
        ? idAttributes.set('popover', POPOVER_MODE_MANUAL)
        : idAttributes;

    const renderTag = (ref: React.RefCallback<OverlayElement> | undefined) => (
      <dialog
        ref={ref}
        // A dialog shown with showModal() in Preview stays open and modal
        // after Preview ends; a fresh element per mode resets it.
        key={previewMode ? 'preview' : 'design'}
        {...generateAttributes(modalAttributes, classes.push('w-modal'))}
      >
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, context)
        )}
      </dialog>
    );

    if (runtimeEnvironment === WebflowEnvironments.DESIGNER) {
      return (
        <DesignerOverlayTarget
          data={data}
          elementId={elementId}
          environmentContext={environmentContext}
          nativeIdPath={nativeIdPath}
          selectedNativeIdPath={environmentContext.get('selectedNativeIdPath')}
          enabled={!previewMode}
          anchorStrategy="none"
          renderTag={renderTag}
        />
      );
    }

    return renderTag(undefined);
  }
);

const config = createDevlinkAtom({
  displayName: 'Modal',
  showEmpty: true,
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Modal'), iconName('Modal')),
  },
  render: Modal,
  fields: {
    // Off when absent, so modals made before the setting existed keep
    // scrolling the page. New modals start off too; authors opt in.
    lockScroll: withSystem(
      {
        label: 'Lock scrolling while open',
        labelVariant: 'hidden',
        defaultValue: false,
      },
      TTypeConstructor(
        qualify(Plugins.name('Modal'), typeName('LabeledCheckbox'))
      )
    ),
    children: withSystem({private: true}, TListOfElement),
  },
});

export default config;
